import { DEFAULT_LOCALE, pickPrimary, toCivilDate } from '@mfw/common';

import type {
  ApiCreateTrainer,
  UiCreateTrainer,
  ApiTrainer,
  UiTrainer,
} from '../../schemas/trainers.schema';
import type { UiTranslation } from '../../schemas/translations.schema';
import type { Locale } from '../../../../@mfw-translations';
import type { ApiRole } from '../../schemas/roles.schema';

import {
  mergeDefaultSocialMedia,
  mapSocialMediaFromApi,
} from './socialMedia.mappers';
import { getLocalizedNameByLocale } from '../../../../@mfw-components/src/utils/locales';
import { mapAddressFromApi } from './address.mappers';
import { mapStatusFromApi } from './status.mappers';
import { getTranslation } from '../../utils';

export function mapTrainerFromApi(trainer?: ApiTrainer): UiTrainer | null {
  if (!trainer) return null;

  const primaryAddress = pickPrimary(trainer?.addresses);
  const primaryContact = pickPrimary(trainer?.contacts);

  return {
    ...trainer,
    address: primaryAddress ? mapAddressFromApi(primaryAddress) : null,
    clientCount: trainer.clientCount ?? 0,
    contact: primaryContact ?? null,
    createdAt: toCivilDate(new Date(trainer.createdAt)),
    email: trainer?.organizationUsers?.[0]?.user?.email
      ? trainer?.organizationUsers?.[0]?.user?.email
      : trainer.email,
    name: trainer.name,
    organizationUserId: trainer?.organizationUsers?.[0]?.userId,
    profileUrl: trainer.settings?.profileUrl ?? undefined,
    role: trainer.roles ? getRoleTranslations(trainer?.roles) : null,
    roleIds: (trainer.roles || []).map((role) => {
      return {
        label: getTranslation(
          (trainer.settings?.locale as Locale) ?? DEFAULT_LOCALE,
          role.translations,
        ),
        value: role.id,
      };
    }),
    socialMedia: mergeDefaultSocialMedia(
      trainer.socialMedia?.map(mapSocialMediaFromApi) || [],
    ),
    status: trainer?.organizationUsers?.[0]
      ? mapStatusFromApi(trainer?.organizationUsers?.[0]?.status)
      : mapStatusFromApi(trainer.status),
    updatedAt: trainer.updatedAt,
  };
}

function getRoleTranslations(roles: ApiRole[]): UiTranslation | null {
  const role = roles[0];
  const translation = role.translations[0];

  return {
    locale: [
      {
        label: getLocalizedNameByLocale(translation.locale),
        value: translation.locale,
      },
    ],
    name: translation.name,
  };
}

export function createTrainerArgs(
  trainer: UiCreateTrainer,
): Partial<ApiCreateTrainer> {
  return {
    ...trainer,
    address: {
      city: trainer.address.city,
      country: trainer.address.country?.[0].value,
      isAddressPublic: trainer.address.isAddressPublic ?? undefined,
      state: undefined,
      street: trainer.address.street ?? undefined,
      zip: trainer.address.zip,
    },
    roleIds: trainer.roleIds?.map((role) => role.value) ?? [],
  };
}
